import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Star, Instagram, Music2, ShoppingBag, MessageCircle, MapPin, Clock, Scissors, Sparkles, Gem, ShieldCheck, Flower2, GraduationCap } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Reveal, MaskedLine, SectionHeading } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { REVIEWS, HERO_FALLBACK } from "@/data/static";

const FEATURED = [
  { name: "Hair Salon", desc: "Haircut, styling, coloring, smoothing & perm.", icon: Scissors, link: "/layanan" },
  { name: "Academy", desc: "Academy All In, Academy Haircut & Academy Coloring.", icon: GraduationCap, link: "/academy" },
  { name: "Body Massage", desc: "Full body massage, reflexology shoulder & foot scrub, back therapy, Miss V.", icon: Sparkles, link: "/layanan" },
  { name: "Nail Art", desc: "Nail art, nail course & gel halal.", icon: Gem, link: "/layanan" },
  { name: "Hijab Room", desc: "Ruangan privat 100% tertutup.", icon: ShieldCheck, link: "/layanan" },
  { name: "Facial & Beauty", desc: "Facial care, dermapeel, acne, melasma & hair removal.", icon: Flower2, link: "/layanan" },
];

export default function Home() {
  const { settings, waLink } = useSettings();
  const [reviews, setReviews] = useState(REVIEWS);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  useEffect(() => {
    api.get("/reviews").then((r) => { if (r.data.length) setReviews(r.data.slice(0, 6)); }).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-brand-burgundy">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #D4AF37 0%, transparent 40%), radial-gradient(circle at 85% 75%, #802232 0%, transparent 45%)" }} />
        <div className="relative mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 pt-32 pb-20 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:pt-40">
          <div>
            <MaskedLine delay={0.15}>
              <p className="overline-label !text-brand-gold-warm">Beauty Starts Here — Est. Bandung</p>
            </MaskedLine>
            <h1 className="mt-6 font-cinzel font-black text-brand-cream leading-[1.05] text-3xl sm:text-4xl lg:text-5xl tracking-tight" data-testid="hero-headline">
              <MaskedLine delay={0.3}>SALON STRAWBERRY</MaskedLine>
              <MaskedLine delay={0.45} innerClassName="text-gold-gradient">OFFICIAL</MaskedLine>
            </h1>
            <MaskedLine delay={0.65} className="mt-6">
              <p className="font-serif italic text-lg sm:text-2xl text-brand-cream/80 max-w-lg">
                More Than Just A Salon — All Your Beauty and Self-Care Needs.
              </p>
            </MaskedLine>
            <MaskedLine delay={0.8} className="mt-5">
              <p className="font-cinzel text-xs sm:text-sm tracking-[0.3em] text-brand-gold">HAIR • BEAUTY • ACADEMY</p>
            </MaskedLine>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <a
                data-testid="hero-book-appointment-btn"
                href={waLink("Halo Salon Strawberry Official! Saya ingin booking appointment.")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 rounded-full bg-brand-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-brand-ink transition-all duration-300 hover:bg-brand-gold-bright hover:shadow-[0_0_35px_rgba(212,175,55,0.45)]"
              >
                Book Your Appointment <ArrowRight size={15} />
              </a>
              <Link
                data-testid="hero-explore-services-btn"
                to="/layanan"
                className="inline-flex items-center gap-3 rounded-full border border-brand-gold/50 px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-brand-cream transition-all duration-300 hover:bg-brand-cream hover:text-brand-burgundy"
              >
                View All Services
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3, duration: 1 }}
              className="mt-14 grid grid-cols-3 gap-6 border-t border-brand-gold/20 pt-8"
              data-testid="hero-stats"
            >
              {[["12+", "Layanan Premium"], ["99%", "Klien Puas"], ["15K+", "Transformasi"]].map(([v, l]) => (
                <div key={l}>
                  <p className="font-serif text-2xl sm:text-4xl font-bold text-brand-gold">{v}</p>
                  <p className="mt-1 text-[10px] sm:text-xs uppercase tracking-[0.2em] text-brand-cream/60">{l}</p>
                </div>
              ))}
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative hidden lg:block"
          >
            <div className="overflow-hidden rounded-t-[10rem] gold-foil-border" data-testid="hero-image-frame">
              <motion.img
                src={imgSrc(settings?.hero_image) || HERO_FALLBACK}
                alt="Interior Salon Strawberry Official"
                style={{ y: imgY, scale: 1.15 }}
                className="aspect-[3/4] w-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 glass-cream rounded-sm px-6 py-4 gold-foil-border shadow-xl">
              <p className="font-cinzel text-xs tracking-[0.25em] text-brand-burgundy">STRAWBERRY BEAUTY BAR</p>
              <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-brand-ink-muted">Sejak 2000 — Kota Bandung</p>
            </div>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* 01 LAYANAN UNGGULAN */}
      <section data-testid="home-featured-services" className="bg-brand-paper py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading index="01" overline="Layanan Unggulan" title="Signature Treatments Kami" description="Enam layanan paling dicintai pelanggan — dikerjakan stylist & terapis bersertifikat." />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURED.map((s, i) => (
              <Reveal key={s.name} delay={(i % 3) * 0.1}>
                <Link to={s.link} data-testid={`home-featured-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="group block h-full bg-brand-cream rounded-sm gold-foil-border p-8 transition-all duration-500 hover:bg-brand-burgundy">
                  <s.icon size={28} className="text-brand-gold transition-transform duration-500 group-hover:scale-110" />
                  <h3 className="mt-5 font-serif text-xl font-semibold text-brand-burgundy transition-colors duration-500 group-hover:text-brand-cream">{s.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-brand-ink-muted transition-colors duration-500 group-hover:text-brand-cream/70">{s.desc}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-brand-gold opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                    Selengkapnya <ArrowRight size={13} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 02 REVIEWS */}
      <section data-testid="home-reviews" className="bg-brand-burgundy py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading index="02" overline="Kata Mereka" title="Review Customer" dark description="Ribuan pelanggan telah mempercayakan momen kecantikan mereka kepada kami." />
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {reviews.map((r, i) => (
              <Reveal key={r.name} delay={(i % 3) * 0.1}>
                <div data-testid={`home-review-${i}`} className="h-full rounded-sm border border-brand-gold/20 bg-brand-cream/[0.04] p-8 backdrop-blur-sm transition-colors duration-500 hover:border-brand-gold/50">
                  <div className="flex gap-1">
                    {Array.from({ length: r.rating }).map((_, s) => (
                      <Star key={s} size={14} className="fill-brand-gold text-brand-gold" />
                    ))}
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-brand-cream/80 italic">"{r.text}"</p>
                  <div className="mt-6 border-t border-brand-gold/15 pt-4">
                    <p className="font-serif text-brand-cream">{r.name}</p>
                    <p className="text-xs uppercase tracking-[0.2em] text-brand-gold">{r.service}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 03 SOCIAL */}
      <section data-testid="home-social" className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
        <SectionHeading index="03" overline="Social Media" title="Ikuti Keseharian Kami" center description="Konten transformasi terbaru, tips kecantikan, dan promo kilat setiap hari." />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: Instagram, name: "Instagram", handle: "@strawberrysalonofficial", href: settings?.instagram_url, note: "Feed & reels transformasi" },
            { icon: Music2, name: "TikTok", handle: "@strawberrysalon_official", href: settings?.tiktok_url, note: "Behind the scenes seru" },
            { icon: MessageCircle, name: "WhatsApp", handle: "Admin Booking", href: waLink("Halo Salon Strawberry Official!"), note: "Fast response booking" },
            { icon: ShoppingBag, name: "Shopee", handle: "Strawberry Official Shop", href: settings?.shopee_url, note: "Produk perawatan asli" },
          ].map((s, i) => (
            <Reveal key={s.name} delay={i * 0.1}>
              <a
                data-testid={`home-social-${s.name.toLowerCase()}`}
                href={s.href || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="group block rounded-sm bg-white gold-foil-border p-7 text-center transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(74,14,23,0.12)]"
              >
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-burgundy text-brand-gold transition-colors duration-500 group-hover:bg-brand-gold group-hover:text-brand-ink">
                  <s.icon size={22} />
                </div>
                <p className="mt-4 font-serif text-lg font-semibold text-brand-burgundy">{s.name}</p>
                <p className="text-xs text-brand-gold">{s.handle}</p>
                <p className="mt-2 text-xs text-brand-ink-muted">{s.note}</p>
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 04 LOKASI */}
      <section data-testid="home-location" className="bg-brand-paper py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <SectionHeading index="04" overline="Lokasi Salon" title="Kunjungi Beauty Destination Kami" />
            <Reveal delay={0.15}>
              <div className="space-y-5">
                <p className="flex gap-4 text-base text-brand-ink"><MapPin size={20} className="text-brand-gold shrink-0 mt-0.5" />{settings?.address || "Jl. Mekar Puspita No.100, Mekarwangi, Kec. Bojongloa Kidul, Kota Bandung"}</p>
                <p className="flex gap-4 text-base text-brand-ink"><Clock size={20} className="text-brand-gold shrink-0 mt-0.5" />{settings?.hours || "Senin - Sabtu: 08.00 - 19.00 WIB • Minggu: 10.00 - 19.00 WIB"}</p>
                <Link
                  data-testid="home-location-book-btn"
                  to="/kontak"
                  className="inline-flex items-center gap-3 rounded-full bg-brand-burgundy px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-brand-cream transition-all duration-300 hover:bg-brand-gold hover:text-brand-ink"
                >
                  Reservasi Sekarang <ArrowRight size={15} />
                </Link>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <div className="overflow-hidden rounded-sm gold-foil-border shadow-[0_20px_60px_rgba(74,14,23,0.15)]" data-testid="home-map-frame">
              <iframe
                title="Lokasi Salon Strawberry Official"
                src={settings?.maps_embed_url || "https://www.google.com/maps?q=Jl.+Mekar+Puspita+No.100,+Mekarwangi,+Kec.+Bojongloa+Kidul,+Kota+Bandung&output=embed"}
                className="h-80 w-full sm:h-96"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
